import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, Customer, gungAddRemoveSpinner, SelectedCustomerService } from 'gung-standard';
import { first, forkJoin } from 'rxjs';
import { CarLinerMysetupService, MySetup } from '../../services/car-liner-mysetup.service';
import { CommonModalService, FormUtilService, OptionsList } from 'gung-common';
import { GungUser as User } from 'gung-standard';

@Component({
    selector: 'car-liner-mysetup-modal',
    templateUrl: './car-liner-mysetup-modal.component.html',
    styleUrls: ['./car-liner-mysetup-modal.component.scss'],
    standalone: false
})
export class CarLinerMysetupModalComponent implements OnInit {
  public form: FormGroup;
  mySetup: MySetup;
  customer: Customer;
  user: User;
  saved = false;
  currentTab = 'CALCULATIONS';

  optionsListLanguage: OptionsList[] = [
    { id: '', name: '' },
    { id: 'en', name: 'English' },
    { id: 'fr', name: 'French' },
    { id: 'es', name: 'Spanish' },
    { id: 'de', name: 'Deutsch' },
    { id: 'it', name: 'Italian' },
    { id: 'se', name: 'Swedish' }
  ];

  constructor(
    public activeModal: NgbActiveModal,
    protected formBuilder: FormBuilder,
    protected selectedCustomerService: SelectedCustomerService,
    protected carLinerMysetupService: CarLinerMysetupService,
    protected formUtilService: FormUtilService,
    protected commonModalService: CommonModalService,
    protected authService: AuthService
  ) {}

  ngOnInit(): void {
    forkJoin({
      mySetup: this.carLinerMysetupService.getMySetup().pipe(first()),
      customer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      user: this.authService.getCurrentUser().pipe(first())
    }).subscribe(({ mySetup, customer, user }) => {
      this.customer = customer;
      this.mySetup = mySetup;
      this.user = user;
      this.initForm();
    });
  }

  initForm() {
    this.form = this.formBuilder.group({
      currency: this.formBuilder.control(
        { value: this.mySetup?.currency || this.customer.extra?.kus?.valkod || undefined, disabled: false },
        [Validators.required]
      ),
      currencyRate: this.formBuilder.control({ value: this.mySetup?.currencyRate || 1, disabled: false }),
      markUp: this.formBuilder.control({ value: this.mySetup?.markUp || undefined, disabled: false }),
      name: this.formBuilder.control({ value: this.mySetup?.name || '', disabled: false }, [Validators.required]),
      address1: this.formBuilder.control({ value: this.mySetup?.address1 || '', disabled: false }),
      address2: this.formBuilder.control({ value: this.mySetup?.address2 || '', disabled: false }),
      zipCode: this.formBuilder.control({ value: this.mySetup?.zipCode || '', disabled: false }),
      city: this.formBuilder.control({ value: this.mySetup?.city || '', disabled: false }),
      organizationNumber: this.formBuilder.control({
        value: this.mySetup?.organizationNumber || '',
        disabled: false
      }),
      email: this.formBuilder.control({ value: this.mySetup?.email || '', disabled: false }),
      telephone: this.formBuilder.control({ value: this.mySetup?.telephone || '', disabled: false }),
      webAddress: this.formBuilder.control({ value: this.mySetup?.webAddress || '', disabled: false }),

      extra: this.formBuilder.group({
        language: this.formBuilder.control({ value: this.mySetup?.extra?.language || undefined, disabled: false }),
        quotations: this.formBuilder.group({
          customer: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.customer || undefined,
            disabled: false
          }),
          yourReference: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.yourReference || undefined,
            disabled: false
          }),
          address1: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.address1 || this.customer.extra?.fr?.ftgpostadr1 || undefined,
            disabled: false
          }),
          notes: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.notes || undefined,
            disabled: false
          }),
          date: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.date || undefined,
            disabled: false
          }),
          expireDate: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.expireDate || undefined,
            disabled: false
          }),
          ourReference: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.ourReference || undefined,
            disabled: false
          }),
          paymentTerms: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.paymentTerms || undefined,
            disabled: false
          }),
          deliveryTerms: this.formBuilder.control({
            value: this.mySetup?.extra?.quotations?.deliveryTerms || undefined,
            disabled: false
          })
        })
      })
    });
  }

  resetForm() {
    const ref = this.commonModalService.openConfirmYesNoModal('RESET', 'CONFIRM_RESET');
    ref.then(
      result => {
        if (result) {
          this.resetFormTab();
          /* this.formUtilService.resetForm(this.form); */
          this.save(this.form);
        }
      },
      () => undefined
    );
  }

  resetFormTab() {
    if (this.currentTab === 'CALCULATIONS') {
      this.form.patchValue({
        currency: null,
        currencyRate: null,
        markUp: null
      });
    } else if (this.currentTab === 'COMPANY_INFORMATION') {
      this.form.patchValue({
        name: null,
        address1: null,
        address2: null,
        zipCode: null,
        city: null,
        organizationNumber: null,
        email: null,
        telephone: null,
        webAddress: null
      });
    } else if (this.currentTab === 'CONDITIONS') {
      this.form.get('extra').patchValue({
        quotations: {
          customer: null,
          yourReference: null,
          address1: null,
          notes: null,
          date: null,
          expireDate: null,
          ourReference: null,
          paymentTerms: null,
          deliveryTerms: null
        }
      });
    }
  }

  tabChange(tab) {
    this.currentTab = tab;
  }

  save(element) {
    gungAddRemoveSpinner(element.target);
    const rawData = this.form.getRawValue();

    this.carLinerMysetupService.saveMySetup(rawData).subscribe(_ => {
      window.location.reload();
      this.saved = true;
      setTimeout(() => {
        this.saved = false;
      }, 3000);
      gungAddRemoveSpinner(element.target);
    });
  }

  crossClick() {
    this.activeModal.dismiss('Cross click');
  }

  close() {
    this.activeModal.close('Closed');
  }
}
